import { useEffect, useRef, useState } from "react";
import { Deduction } from "@/core/entities/deduction";
import { HasAnyPermission } from "@/presentation/workspace";
import { WorkspacePermissions } from "@/core/permissions";
import { useDeductionDrawer, useDeductionItemExport } from "@/presentation/deduction/hooks";
import {
  Card,
  CardHeader,
  Flex,
  Heading,
  Button,
  Icon,
  Icons,
  CardContent,
  Division,
  Input,
  EmptyState,
  Paragraph,
  Span,
  Chip,
  Pagination,
  useToast,
} from "@ir/ui";
import { DeductionItem } from "@/core/entities/deduction/deduction/DeductionItem";
import { IColumnAlias, IDeductionItemRepositoryResponse } from "@/core/repositories/deduction";
import { GetDeductionItemUseCase } from "@/core/usecases/deduction/GetDeductionItem.useCase";
import ChargeBreakdownTable from "./ChargeBreakdownTable";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { DeductionItemRepository } from "@/infrastructure/repositories/deduction";
import { getColumnProps, CellType } from "./utils/ChargeBreakdownTable.utils";
import { useWorkspace, useWindowSize } from "@/hooks";
import { DateService, NumberFormatterService } from "@/core/services";
import { DateAdapter } from "@/infrastructure/adapters/dayJs";
import { NumberFormatterAdapter } from "@/infrastructure/adapters/number";
import classNames from "classnames";
import AddChargebreakdownModal from "./AddChargebreakdown/AddChargebreakdownModal";
import { createLogger } from "@/infrastructure/services/logger";

// GraphQL client
const client = GraphQlAdapter.getInstance().getClient();
// Deduction item repository
const deductionItemRepository = new DeductionItemRepository(client);

const SEARCH_MIN_LENGTH = 2;

const ITEMS_PER_PAGE = 50;

export interface DeductionChargeBreakdownProps {
  deduction: Deduction;
}

const DeductionChargeBreakdown = ({ deduction }: DeductionChargeBreakdownProps) => {
  const logger = createLogger("DeductionChargeBreakdown");
  const { settings: workspaceSettings } = useWorkspace();
  const {
    isDeductionsLoading,
    setIsDocumentViewerOpen,
    isDocumentViewerOpen,
    setViewerDocuments,
    viewerDocuments,
    drawerInfo,
    setCloseDocumentViewer,
    isDrawerModal,
  } = useDeductionDrawer();
  const [deductionItems, setDeductionItems] = useState<DeductionItem[]>([]);
  const [deductionItemColumns, setDeductionItemColumns] = useState<IColumnAlias[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeductionItemLoading, setIsDeductionItemLoading] = useState(false);
  const [isDeductionItemError, setIsDeductionItemError] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [isSearchInvalid, setIsSearchInvalid] = useState<boolean>(false);
  const [isDeductionItemExpanded, setIsDeductionItemExpanded] = useState<boolean>(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { handleDeductionItemExport, isDeductionItemExporting, setIsDeductionItemExporting } = useDeductionItemExport();
  const { width } = useWindowSize();

  useEffect(() => {
    setSearchValue("");
    setSearchInputValue("");
    setIsDeductionItemError(false);
    setCurrentPage(1);
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    const getDeductionItems = async () => {
      if (!deduction?.id) return;
      setIsDeductionItemLoading(true);
      try {
        const deductionItemResponse = await new GetDeductionItemUseCase(deductionItemRepository).execute({
          deduction_id: deduction?.id,
        });
        setDeductionItems(deductionItemResponse?.deductionItems ?? []);
        setDeductionItemColumns(deductionItemResponse?.columns ?? []);
      } catch (error) {
        logger.error("Error getting deduction items", error);
        setIsDeductionItemError(true);
      }

      setIsDeductionItemLoading(false);
    };

    getDeductionItems();

    // cleanup unmount
    return () => {
      setDeductionItems([]);
      setDeductionItemColumns([]);
      setCurrentPage(1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deduction?.id]);

  const isLoading = isDeductionsLoading || isDeductionItemLoading;

  const isColumnVisible = (column: any, item: DeductionItem) => {
    const rawValue = item[column.dataIndex as keyof DeductionItem];
    return (
      rawValue !== null &&
      rawValue !== undefined &&
      !column.hidden &&
      deductionItemColumns.some(col => col.key === column.dataIndex)
    );
  };

  const handleSearch = () => {
    const value = searchValue;
    const _deductionItems = [...deductionItems];

    if (!value || value.trim().length === 0 || value.trim().length < SEARCH_MIN_LENGTH) {
      return _deductionItems;
    }
    const results = _deductionItems.filter(item => {
      return getColumnProps().some(column => {
        if (!isColumnVisible(column, item)) {
          return false;
        }

        let formattedValue: string;

        switch (column.cellType) {
          case CellType.DATE: {
            const dateFormat = workspaceSettings?.displayDateFormat;
            if (dateFormat) {
              formattedValue = new DateService(new DateAdapter()).format(
                item[column.dataIndex as keyof DeductionItem] as string,
                dateFormat
              );
            } else {
              formattedValue = item[column.dataIndex as keyof DeductionItem]?.toString() || "";
            }
            break;
          }
          case CellType.NUMBER: {
            const format = workspaceSettings?.displayNumberFormat;
            if (format) {
              formattedValue = new NumberFormatterService(new NumberFormatterAdapter()).format(
                item[column.dataIndex as keyof DeductionItem] as number,
                format
              );
            } else {
              formattedValue = item[column.dataIndex as keyof DeductionItem]?.toString() || "";
            }
            break;
          }
          case CellType.TEXT: {
            formattedValue = item[column.dataIndex as keyof DeductionItem]?.toString() || "";
            break;
          }
          default: {
            formattedValue = item[column.dataIndex as keyof DeductionItem]?.toString() || "";
          }
        }

        return formattedValue.toLowerCase().includes(value.toString().toLowerCase());
      });
    });
    return results;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(event.currentTarget.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      if (event.currentTarget.value.length === 0) {
        handleClear();
        return;
      } else if (event.currentTarget.value.length > 0 && event.currentTarget.value.length < SEARCH_MIN_LENGTH) {
        setIsSearchInvalid(true);
        return;
      }
      setIsSearchInvalid(false);
      setSearchValue(event.currentTarget.value);
      setCurrentPage(1);
    }
  };

  const handleClear = () => {
    setSearchInputValue("");
    setSearchValue("");
    setIsSearchInvalid(false);
    setCurrentPage(1);
  };

  const handleChargeBreakdownExpand = () => {
    setIsDeductionItemExpanded(!isDeductionItemExpanded);
    if (width > 1200 && !isDeductionItemExpanded) {
      handleOpenDocumentViewer();
    }
  };

  const handleOpenDocumentViewer = async () => {
    if (!isDocumentViewerOpen) {
      const documents = deduction?.documents;
      deduction.documents = viewerDocuments ?? documents;
      setIsDocumentViewerOpen(true);
      setViewerDocuments(documents);
    }
  };

  const handleCloseDocumentViewer = () => {
    setCloseDocumentViewer();
  };

  const handleHandleSearch = () => {
    setCurrentPage(1);
    setSearchValue(searchInputValue);
  };

  const deductionChargeBreakdownClass = classNames("bg-white", {
    expanded: isDeductionItemExpanded,
  });

  const deductionItemsWithSearch = handleSearch() ?? [];

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentDeductionItems = deductionItemsWithSearch.slice(startIndex, endIndex);

  const { error: errorToast } = useToast();
  const handleChargeBreakdownExport = async () => {
    try {
      const response = await handleDeductionItemExport(deduction?.id, drawerInfo?.invoiceNumber as string);
      if (response?.success) {
        globalThis.open(response.signedUrl, "_blank");
      } else {
        errorToast({
          content: "Failed to export charge breakdown",
        });
      }
    } catch {
      errorToast({
        content: "Failed to export charge breakdown",
      });
    } finally {
      setIsDeductionItemExporting(false);
    }
  };

  const isExportDisabled = isLoading || deductionItems.length === 0;

  // Charge Breakdown Table - Scroll to top on pagination
  useEffect(() => {
    const scrollToTop = () => {
      const chargeBreakdownTableContainer = document.querySelector(".chargebreakdown-table-container") as HTMLElement;
      if (chargeBreakdownTableContainer) {
        chargeBreakdownTableContainer.scrollTop = 0;
      }
    };

    requestAnimationFrame(scrollToTop);
  }, [currentPage]);

  const shouldShowChargeBreakdown =
    !isDeductionsLoading && Boolean(deduction?.id) && deduction?.isDeductionDrawerEditable();

  return (
    <Card
      className={deductionChargeBreakdownClass}
      style={{ width: isDocumentViewerOpen && isDeductionItemExpanded && isDrawerModal ? "50%" : "100%" }}
    >
      <CardHeader className="py-2">
        <Flex justifyContent="space-between" align="center" className={`pb-2 ${isDeductionItemExpanded ? "py-3" : ""}`}>
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Charge Breakdown{" "}
            {!isLoading && isDeductionItemExpanded && deduction?.deductionSubType && (
              <Chip color="secondary" label={deduction?.deductionSubType} size="small" className="ms-2" />
            )}
          </Heading>
        </Flex>
        <Flex justifyContent="space-between" align="center">
          <Division
            className="charge-breakdown-search"
            style={{ width: isDeductionItemExpanded ? 240 : 320, position: "relative" }}
          >
            <Input
              size="medium"
              type="search"
              placeholder="Search"
              allowClear
              isWarning={isSearchInvalid}
              onClear={handleClear}
              onKeyDown={handleKeyDown}
              maxLength={30}
              showCharCount={true}
              readOnly={isLoading}
              value={searchInputValue}
              onChange={handleChange}
              onSearch={handleHandleSearch}
            />
            {isSearchInvalid && (
              <Span className="text-grey-500 text-xs">Search must be at least {SEARCH_MIN_LENGTH} characters long</Span>
            )}
          </Division>
          <Division className="flex flex-align-stretch">
            <Button
              size="medium"
              key={isDeductionItemExpanded ? "Hide" : "View"}
              variant="outline"
              color="grey"
              className="mx-1"
              onPress={handleChargeBreakdownExpand}
            >
              <Icon size="medium" name={isDeductionItemExpanded ? Icons.Minimize01 : Icons.Maximize01} />
            </Button>

            {width > 1200 && isDeductionItemExpanded && !isLoading && deduction?.documents?.length > 0 && (
              <Button
                size="medium"
                variant="outline"
                color="grey"
                onPress={isDocumentViewerOpen ? handleCloseDocumentViewer : handleOpenDocumentViewer}
                className="mx-1"
              >
                <Icon size="medium" slot="start" name={Icons.File} />
                {isDocumentViewerOpen ? "Hide Documents" : "View Documents"}
              </Button>
            )}
            {!isExportDisabled && (
              <Button
                isDisabled={isExportDisabled || isDeductionItemExporting}
                color="primary"
                variant={isExportDisabled || isDeductionItemExporting ? "solid" : "outline"}
                size="medium"
                className="mx-1"
                onPress={handleChargeBreakdownExport}
              >
                <Icon
                  name={isDeductionItemExporting ? Icons.Loading : Icons.Download02}
                  size="medium"
                  slot="start"
                  className={isDeductionItemExporting ? "icon-fast-loading" : ""}
                />
                Export
              </Button>
            )}

            <HasAnyPermission requiredPermissions={[WorkspacePermissions.DEDUCTIONS_CHARGE_BREAKDOWN_ADD]}>
              <AddChargebreakdownModal
                deductionId={deduction?.id}
                shouldShowChargeBreakdown={shouldShowChargeBreakdown}
                onSuccess={async () => {
                  const getDeductionItemUseCase = new GetDeductionItemUseCase(deductionItemRepository);
                  let deductionItemResponse: IDeductionItemRepositoryResponse;
                  try {
                    setIsDeductionItemLoading(true);
                    deductionItemResponse = await getDeductionItemUseCase.execute(
                      { deduction_id: deduction?.id },
                      { refetch: true }
                    );

                    setDeductionItems(deductionItemResponse?.deductionItems ?? []);
                    setDeductionItemColumns(deductionItemResponse?.columns ?? []);
                  } catch (error) {
                    logger.error("Error getting deduction items", error);
                    setIsDeductionItemError(true);
                  } finally {
                    setIsDeductionItemLoading(false);
                  }
                }}
              />
            </HasAnyPermission>
          </Division>
        </Flex>
      </CardHeader>

      <CardContent>
        <ChargeBreakdownTable
          deductionId={deduction?.id}
          deductionItems={currentDeductionItems}
          deductionItemColumns={deductionItemColumns}
          isLoading={isLoading}
          keywords={searchValue ? [searchValue] : []}
          isExpanded={isDeductionItemExpanded}
          currentPage={currentPage}
          pageSize={ITEMS_PER_PAGE}
        />
        <Division className="mt-3">
          {deductionItemsWithSearch.length > ITEMS_PER_PAGE && !isLoading && (
            <Pagination
              totalItems={deductionItemsWithSearch.length}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          )}
        </Division>

        {!isLoading && deductionItemsWithSearch.length === 0 && !isDeductionItemError && (
          <EmptyState
            className="bg-white border-1 border-color-grey-25 py-3 flex-1"
            icon={Icons.Browser}
            title={
              <Heading level={6} className="text-grey-700 font-bold mt-3">
                No charge breakdown
              </Heading>
            }
            description={
              <Paragraph className="text-grey-500 font-regular mb-0">
                No charge breakdown found for this deduction
              </Paragraph>
            }
          />
        )}
        {isDeductionItemError && (
          <EmptyState
            className="bg-white border-1 border-color-grey-25 py-3 flex-1"
            icon={Icons.AlertCircle}
            title={
              <Heading level={6} className="text-grey-700 font-bold mt-3">
                Something went wrong
              </Heading>
            }
            description={
              <Paragraph className="text-grey-500 font-regular mb-0">Failed to load charge breakdown</Paragraph>
            }
          />
        )}
      </CardContent>
    </Card>
  );
};

export default DeductionChargeBreakdown;
